import React, { useState, useEffect, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { PaperPlaneRight, Sparkle, Plus, Clock, Trash, PencilSimple, Check, X, DotsThreeVertical } from '@phosphor-icons/react';
import AIActionRenderer from '../../components/AIActionRenderer';
import { parseTimestamp } from '@/utils/datetime';
import ActivityPulse from '@/components/ActivityPulse';

const VCChat = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const messagesEndRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    fetchSessions();
    // Check for AI context from Ask AI button
    const ctx = sessionStorage.getItem('ai_context');
    if (ctx) {
      sessionStorage.removeItem('ai_context');
      setTimeout(() => handleSendMessage(ctx), 500);
    }
  }, []);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    const handleClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpenId(null); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await api.get('/vc/chat/sessions');
      setSessions(res.data);
    } catch (err) { console.error(err); }
  };

  const loadSession = async (sid) => {
    try {
      const res = await api.get(`/vc/chat/history/${sid}`);
      setMessages(res.data);
      setSessionId(sid);
      setMenuOpenId(null);
    } catch (err) { toast.error('Failed to load chat'); }
  };

  const startNewChat = () => { setMessages([]); setSessionId(''); setMenuOpenId(null); };

  const deleteSession = async (sid) => {
    if (!confirm('Delete this chat?')) return;
    try {
      await api.delete(`/vc/chat/sessions/${sid}`);
      toast.success('Chat deleted');
      if (sessionId === sid) startNewChat();
      fetchSessions();
    } catch (err) { toast.error('Failed to delete'); }
    setMenuOpenId(null);
  };

  const startRename = (sid, currentTitle) => {
    setEditingId(sid);
    setEditTitle(currentTitle);
    setMenuOpenId(null);
  };

  const saveRename = async () => {
    if (!editTitle.trim()) { setEditingId(null); return; }
    try {
      await api.patch(`/vc/chat/sessions/${editingId}`, { title: editTitle.trim() });
      fetchSessions();
    } catch (err) { toast.error('Failed to rename'); }
    setEditingId(null);
  };

  const quickPrompts = [
    'What is my portfolio MoIC today?',
    'Which companies need attention?',
    'How do my tracked startups compare to my portfolio?',
  ];

  const handleSendMessage = async (message) => {
    if (!message.trim()) return;
    const userMessage = { role: 'user', content: message };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    try {
      const response = await api.post('/vc/chat', { message, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
      if (!sessionId) { setSessionId(response.data.session_id); fetchSessions(); }
    } catch (error) { toast.error('Failed to get AI response'); }
    finally { setLoading(false); }
  };

  const formatTime = (ts) => {
    const d = parseTimestamp(ts);
    if (!d) return '';
    const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="h-full flex">
      {/* Sidebar */}
      <div className="w-[240px] bg-background border-r border-border flex flex-col shrink-0" data-testid="chat-sidebar">
        <div className="p-3 border-b border-border">
          <button onClick={startNewChat} className="w-full flex items-center gap-2 px-3 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors" data-testid="new-chat-btn">
            <Plus size={14} weight="bold" /> New Chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {sessions.length === 0 ? (
            <div className="text-xs text-text-muted text-center py-8">No chats yet. Start a conversation!</div>
          ) : sessions.map(s => (
            <div key={s.session_id} className={`group relative rounded-lg transition-colors ${sessionId === s.session_id ? 'bg-primary/10' : 'hover:bg-surface'}`}>
              {editingId === s.session_id ? (
                <div className="flex items-center gap-1 px-2 py-2">
                  <input
                    type="text" value={editTitle} onChange={e => setEditTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditingId(null); }}
                    className="flex-1 bg-background border border-primary rounded px-2 py-1 text-xs text-text-primary focus:outline-none"
                    autoFocus
                  />
                  <button onClick={saveRename} className="p-1 text-success hover:bg-success/10 rounded"><Check size={12} weight="bold" /></button>
                  <button onClick={() => setEditingId(null)} className="p-1 text-text-muted hover:bg-surface rounded"><X size={12} weight="bold" /></button>
                </div>
              ) : (
                <button
                  onClick={() => loadSession(s.session_id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-xs ${sessionId === s.session_id ? 'text-primary' : 'text-text-secondary'}`}
                  data-testid={`session-${s.session_id}`}
                >
                  <div className="truncate font-medium pr-6">{s.title}</div>
                  <div className="flex items-center gap-1 mt-1 text-[10px] text-text-muted">
                    <Clock size={10} /> {formatTime(s.last_timestamp)}
                    <span className="ml-auto">{s.message_count} msgs</span>
                  </div>
                </button>
              )}
              {editingId !== s.session_id && (
                <div className="absolute right-1 top-2" ref={menuOpenId === s.session_id ? menuRef : null}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setMenuOpenId(menuOpenId === s.session_id ? null : s.session_id); }}
                    className="p-1 rounded text-text-muted opacity-0 group-hover:opacity-100 hover:text-text-primary hover:bg-background transition-all"
                  >
                    <DotsThreeVertical size={14} weight="bold" />
                  </button>
                  {menuOpenId === s.session_id && (
                    <div className="absolute right-0 top-6 bg-surface border border-border rounded-lg shadow-lg z-50 w-[120px] py-1">
                      <button onClick={() => startRename(s.session_id, s.title)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-text-secondary hover:bg-background hover:text-text-primary" data-testid={`rename-${s.session_id}`}>
                        <PencilSimple size={12} /> Rename
                      </button>
                      <button onClick={() => deleteSession(s.session_id)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-danger hover:bg-danger/10" data-testid={`delete-${s.session_id}`}>
                        <Trash size={12} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex-1 overflow-y-auto p-6">
          {messages.length === 0 && (
            <div className="max-w-4xl mx-auto mt-8 sm:mt-12">
              <ActivityPulse compact />
              <div className="text-center mb-10 mt-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkle size={32} className="text-primary" weight="bold" />
                </div>
                <h2 className="text-3xl font-heading font-bold text-text-primary mb-3">
                  Good morning, {user.full_name?.split(' ')[0]}.
                </h2>
                <p className="text-text-secondary">Ask anything about your portfolio, returns, or tracked startups</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {quickPrompts.map((prompt, i) => (
                  <button key={i} onClick={() => handleSendMessage(prompt)} className="p-4 bg-surface border border-border rounded-lg text-left text-sm text-text-secondary hover:border-primary/50 hover:text-text-primary transition-all" data-testid={`quick-prompt-${i}`}>
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="max-w-3xl mx-auto space-y-5">
            {messages.map((msg, index) => (
              <div key={index} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`message-${index}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkle size={16} className="text-white" weight="bold" />
                  </div>
                )}
                <div className={`px-4 py-3 rounded-xl max-w-[80%] ${msg.role === 'user' ? 'bg-primary text-white rounded-tr-sm' : 'bg-surface border border-border rounded-tl-sm text-text-primary'}`}>
                  {msg.role === 'assistant' ? <AIActionRenderer content={msg.content} /> : <p className="text-sm whitespace-pre-wrap">{msg.content}</p>}
                </div>
                {msg.role === 'user' && (
                  <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0 text-primary text-sm font-medium mt-0.5">
                    {user.full_name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0"><Sparkle size={16} className="text-white" weight="bold" /></div>
                <div className="px-4 py-3 bg-surface border border-border rounded-xl rounded-tl-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input — clean, no grey box */}
        <div className="border-t border-border p-4">
          <div className="max-w-3xl mx-auto flex items-center gap-3">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage(inputMessage)}
              placeholder="Ask anything about your portfolio..."
              className="flex-1 bg-transparent border border-border rounded-xl px-4 py-3 text-text-primary placeholder-text-muted focus:outline-none focus:border-primary text-sm transition-colors"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              onClick={() => handleSendMessage(inputMessage)}
              disabled={!inputMessage.trim() || loading}
              className="w-11 h-11 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 flex-shrink-0"
              data-testid="send-message-button"
            >
              <PaperPlaneRight size={18} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCChat;
